import React, { useState, useEffect, useCallback } from 'react';
import { fetchGet, fetchPatch } from '../../Util/fetchAPI';

import { ReactComponent as MergeSvg }  from '../../../graphics/merge.svg';
import { ReactComponent as InhumanSpeedSvg }  from '../../../graphics/blink.svg';
import useInterval from '../../Util/useInterval';
import { useSnackbar } from 'notistack';

export enum FlagStatus {
  Unresolved = 'unresolved',
  Allowed = 'allowed',
};

export enum FlagIssue {
  PositionOverlap = 'overlap',
  InhumanSpeed = 'inhuman-speed',
  LongInterpolation = 'long-interpolation',
};

export type FlagMetaData = {
  other_task?: string;
  average_speed?: number;
  speed?: number;
};

export type Flag = {
  id: string;
  start: number;
  end: number;
  status: FlagStatus;
  issue: FlagIssue;
  metadata: FlagMetaData;
};

export function getFlagIcon(flag: Flag) {
  return flag.issue === FlagIssue.PositionOverlap ? <MergeSvg /> : <InhumanSpeedSvg />;
}

const fetchTimeMs: number = 45000;

export default function useValidationFlags(
  dRunId: string,
  dTaskId: string,
  baseLabelUrl: string = '/gateway/label-assistant-v2/api/v1/label'
) {
  const [runId] = useState<string>(dRunId);
  const [taskId] = useState<string>(dTaskId);
  const [flags, setFlags] = useState<Flag[]>([]);
  
  const { enqueueSnackbar } = useSnackbar();

  const removeFlags = useCallback((fromFrameId: number) => {
    setFlags(prev => {
      return prev.filter(f => f.start <= fromFrameId);
    });
  }, []);

  const setFlagStatus = useCallback((flag: Flag) => {
    const newStatus: FlagStatus = flag.status === FlagStatus.Unresolved ? FlagStatus.Allowed : FlagStatus.Unresolved;
    fetchPatch(
      baseLabelUrl+'/run/'+runId+
      '/player/'+taskId+
      '/flags/'+flag.id,
      { status: newStatus }
    ).then((newFlag: Flag) => {
      setFlags(prev => {
        const findFlag: Flag | undefined = prev.find(x => x.id === flag.id);
        if(findFlag) {
          findFlag.status = newStatus;
        }
        return [...prev];
      });
      console.log("[Flag Status Changed]", newFlag);
    });
  }, [baseLabelUrl, runId, taskId]);

  const fetch = useCallback(() => {
    fetchGet(
      baseLabelUrl+'/run/'+runId+
      '/player/'+taskId+
      '/flags'
    ).then((flags: Flag[] | undefined) => {
      console.log("[Flags] fetch result: ", flags);
      if(flags) {
        setFlags(prev => {
          if(
            prev.find(x => !flags.find(y => x.id === y.id)) ||
            flags.find(x => !prev.find(y => x.id === y.id))
          ) {
            enqueueSnackbar('Updated validation flags', {
              variant: 'info',
              autoHideDuration: 2000,
            });
          }
          return flags;
        });
      } else {
        enqueueSnackbar('Failed to fetch validation flags', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      }
    });
  }, [baseLabelUrl, enqueueSnackbar, runId, taskId]);

  useEffect(() => {
    // Initially poll all flags
    fetch();
  }, [fetch]);

  // Fetch flags every x seconds
  useInterval(() => {
    fetch();
  }, fetchTimeMs);

  return { flags, setFlagStatus, removeFlags };
};
